import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  ClockIcon,
  PencilSquareIcon,
  LockClosedIcon,
  LockOpenIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ForwardIcon
} from '@heroicons/react/24/outline';

import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

// Pomodoro settings
const WORK_DURATION = 25 * 60; // 25 dakika
const BREAK_DURATION = 5 * 60; // 5 dakika
const LONG_BREAK_DURATION = 15 * 60; // 15 dakika (4 pomodoro sonrası)

const TopicDetailPage = () => {
  const { topicProgressId } = useParams();
  const navigate = useNavigate();

  const [topicProgress, setTopicProgress] = useState(null);
  const [topic, setTopic] = useState(null);
  const [prerequisites, setPrerequisites] = useState([]);
  const [unlockedBy, setUnlockedBy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Study log form
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(30);
  const [progress, setProgress] = useState(0);
  const [activities, setActivities] = useState([]);
  const [questionsAttempted, setQuestionsAttempted] = useState(0);
  const [questionsCorrect, setQuestionsCorrect] = useState(0);
  const [note, setNote] = useState('');

  // Pomodoro state
  const [timerMode, setTimerMode] = useState('idle'); // 'idle' | 'work' | 'break' | 'paused'
  const [timeRemaining, setTimeRemaining] = useState(WORK_DURATION);
  const [pomodoroCount, setPomodoroCount] = useState(0);
  const intervalRef = useRef(null);
  const STORAGE_KEY = `pomodoro_${topicProgressId}`;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const subjectColors = {
    'Matematik': 'bg-blue-500',
    'Fizik': 'bg-purple-500',
    'Kimya': 'bg-green-500',
    'Biyoloji': 'bg-pink-500'
  };

  const activityOptions = [
    { id: 'video', label: 'Konu anlatimi izledim', emoji: '📺' },
    { id: 'questions', label: 'Soru cozdim', emoji: '✏️' },
    { id: 'review', label: 'Tekrar yaptim', emoji: '🔄' },
    { id: 'notes', label: 'Not aldim', emoji: '📝' },
    { id: 'book', label: 'Kitap/kaynak okudum', emoji: '📚' }
  ];

  useEffect(() => {
    fetchTopicDetail();
  }, [topicProgressId]);

  const fetchTopicDetail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/ayt-journey/topic/${topicProgressId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        setTopicProgress(data.topicProgress);
        setTopic(data.topic);
        setPrerequisites(data.prerequisites);
        setUnlockedBy(data.unlockedBy);
        setProgress(data.topicProgress.progress || 0);
      }
    } catch (error) {
      console.error('Konu detay yuklenirken hata:', error);
      alert('Konu detayi yuklenemedi');
      navigate('/ayt-journey');
    } finally {
      setLoading(false);
    }
  };

  // Pomodoro helper functions
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const playNotificationSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;

      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        // Second beep
        const osc2 = audioContext.createOscillator();
        osc2.connect(gainNode);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        osc2.start();
        setTimeout(() => osc2.stop(), 200);
      }, 200);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, []);

  const saveToLocalStorage = useCallback((data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [STORAGE_KEY]);

  const autoSaveStudyLog = useCallback(async (durationMinutes) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/ayt-journey/topic/${topicProgressId}/log`,
        {
          durationMinutes,
          activities: ['pomodoro'],
          progressAfter: progress,
          note: `Pomodoro tamamlandi`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTopicDetail();
    } catch (error) {
      console.error('Pomodoro log kaydetme hatasi:', error);
    }
  }, [API_URL, topicProgressId, progress]);

  const onPomodoroComplete = useCallback(async () => {
    playNotificationSound();
    const newCount = pomodoroCount + 1;
    setPomodoroCount(newCount);
    await autoSaveStudyLog(WORK_DURATION / 60);
    const breakDuration = newCount % 4 === 0 ? LONG_BREAK_DURATION : BREAK_DURATION;
    setTimerMode('break');
    setTimeRemaining(breakDuration);
    saveToLocalStorage({ mode: 'break', startTime: Date.now(), duration: breakDuration, pomodoroCount: newCount });
  }, [pomodoroCount, autoSaveStudyLog, playNotificationSound, saveToLocalStorage]);

  // Pomodoro timer controls
  const startTimer = () => {
    setTimerMode('work');
    setTimeRemaining(WORK_DURATION);
    saveToLocalStorage({ mode: 'work', startTime: Date.now(), duration: WORK_DURATION, pomodoroCount });
  };

  const pauseTimer = () => {
    clearInterval(intervalRef.current);
    setTimerMode('paused');
    saveToLocalStorage({ mode: 'paused', timeRemaining, pomodoroCount });
  };

  const resumeTimer = () => {
    setTimerMode('work');
    saveToLocalStorage({ mode: 'work', startTime: Date.now(), duration: timeRemaining, pomodoroCount });
  };

  const cancelTimer = () => {
    clearInterval(intervalRef.current);
    setTimerMode('idle');
    setTimeRemaining(WORK_DURATION);
    localStorage.removeItem(STORAGE_KEY);
  };

  const skipBreak = () => {
    clearInterval(intervalRef.current);
    setTimerMode('idle');
    setTimeRemaining(WORK_DURATION);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setPomodoroCount(data.pomodoroCount || 0);
        if (data.mode === 'paused') {
          setTimerMode('paused');
          setTimeRemaining(data.timeRemaining);
        } else if (data.mode === 'work' || data.mode === 'break') {
          const elapsed = Math.floor((Date.now() - data.startTime) / 1000);
          const remaining = data.duration - elapsed;
          if (remaining > 0) {
            setTimerMode(data.mode);
            setTimeRemaining(remaining);
          } else {
            // Timer completed while away
            if (data.mode === 'work') {
              onPomodoroComplete();
            } else {
              setTimerMode('idle');
              setTimeRemaining(WORK_DURATION);
              localStorage.removeItem(STORAGE_KEY);
            }
          }
        }
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [STORAGE_KEY]);

  // Timer interval
  useEffect(() => {
    if (timerMode === 'work' || timerMode === 'break') {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            if (timerMode === 'work') {
              onPomodoroComplete();
            } else {
              playNotificationSound();
              setTimerMode('idle');
              setTimeRemaining(WORK_DURATION);
              localStorage.removeItem(STORAGE_KEY);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [timerMode, onPomodoroComplete, playNotificationSound, STORAGE_KEY]);

  const handleActivityToggle = (activityId) => {
    setActivities(prev =>
      prev.includes(activityId)
        ? prev.filter(a => a !== activityId)
        : [...prev, activityId]
    );
  };

  const handleSaveStudyLog = async () => {
    const durationMinutes = (hours * 60) + minutes;

    if (durationMinutes === 0 && activities.length === 0) {
      alert('Lutfen en az bir aktivite secin veya sure girin');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ayt-journey/topic/${topicProgressId}/log`,
        {
          durationMinutes,
          progressAfter: progress,
          activities,
          questionsAttempted: activities.includes('questions') ? questionsAttempted : 0,
          questionsCorrect: activities.includes('questions') ? questionsCorrect : 0,
          note
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Update local state
        setTopicProgress(prev => ({
          ...prev,
          ...response.data.data.topicProgress,
          studyLogs: {
            logs: [response.data.data.log, ...(prev.studyLogs?.logs || [])]
          }
        }));

        // Reset form
        setHours(0);
        setMinutes(30);
        setActivities([]);
        setQuestionsAttempted(0);
        setQuestionsCorrect(0);
        setNote('');

        alert('Calisma kaydi eklendi!');
      }
    } catch (error) {
      console.error('Study log kaydetme hatasi:', error);
      alert('Kaydetme sirasinda bir hata olustu');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteToggle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ayt-journey/progress/${topicProgressId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTopicProgress(prev => ({
          ...prev,
          status: response.data.data.topicProgress.status,
          progress: response.data.data.topicProgress.progress
        }));
        setProgress(response.data.data.topicProgress.progress);
      }
    } catch (error) {
      console.error('Konu tamamlama hatasi:', error);
      alert('Bir hata olustu');
    }
  };

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m} dk`;
    if (m === 0) return `${h} sa`;
    return `${h} sa ${m} dk`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Konu yukleniyor...</p>
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Konu bulunamadi</p>
      </div>
    );
  }

  const isCompleted = topicProgress?.status === 'completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/ayt-journey')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Geri
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className={`inline-block px-3 py-1 ${subjectColors[topic.subject]} text-white text-sm rounded-full mb-2`}>
                  {topic.subject}
                </span>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{topic.name}</h1>
              </div>
              <button
                onClick={handleCompleteToggle}
                className={`p-3 rounded-full transition ${
                  isCompleted ? 'bg-green-100 hover:bg-green-200' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {isCompleted ? (
                  <CheckCircleSolid className="h-8 w-8 text-green-500" />
                ) : (
                  <div className="h-8 w-8 rounded-full border-3 border-gray-400"></div>
                )}
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Ilerleme: {progress}%</span>
                <span className="text-sm text-gray-500">
                  Tahmini: {topic.estimatedHours || 3} saat | Calisilan: {formatDuration(topicProgress?.totalStudyMinutes || 0)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`${subjectColors[topic.subject]} h-3 rounded-full transition-all duration-300`}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Topic Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Zorluk</p>
                <p className="font-semibold text-gray-900">{topic.difficulty || 'Orta'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">AYT Ortalama</p>
                <p className="font-semibold text-gray-900">{topic.avgQuestionCount || 0} soru</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Cozulen Soru</p>
                <p className="font-semibold text-gray-900">{topicProgress?.questionsAttempted || 0}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Dogru</p>
                <p className="font-semibold text-green-600">{topicProgress?.questionsCorrect || 0}</p>
              </div>
            </div>

            {/* Prerequisites & Unlocks */}
            {(prerequisites.length > 0 || unlockedBy.length > 0) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                {prerequisites.length > 0 && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      <LockOpenIcon className="h-4 w-4 inline mr-1" />
                      Onkosullar:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {prerequisites.map(prereq => (
                        <span
                          key={prereq.id}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            prereq.completed
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {prereq.name} {prereq.completed ? '✓' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {unlockedBy.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      <LockClosedIcon className="h-4 w-4 inline mr-1" />
                      Acilan Konular:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {unlockedBy.map(unlocked => (
                        <span
                          key={unlocked.id}
                          className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                        >
                          {unlocked.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pomodoro Timer Widget */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="text-2xl">🍅</span> Pomodoro Timer
            </h3>
            <span className="text-sm text-gray-500">
              Tamamlanan: {pomodoroCount} pomodoro
            </span>
          </div>

          {/* Timer Display */}
          <div className="text-center mb-6">
            <div className={`text-6xl font-bold font-mono ${
              timerMode === 'break' ? 'text-green-600' :
              timerMode === 'work' ? 'text-red-600' : 'text-gray-400'
            }`}>
              {formatTime(timeRemaining)}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {timerMode === 'idle' && 'Baslamak icin butona tiklayin'}
              {timerMode === 'work' && 'Calisma zamani - Odaklan!'}
              {timerMode === 'break' && 'Mola zamani - Dinlen!'}
              {timerMode === 'paused' && 'Duraklatildi'}
            </p>
          </div>

          {/* Progress Ring */}
          <div className="flex justify-center mb-6">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  stroke={timerMode === 'break' ? '#22c55e' : timerMode === 'work' ? '#ef4444' : '#9ca3af'}
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 56}
                  strokeDashoffset={2 * Math.PI * 56 * (1 - timeRemaining / (timerMode === 'break' ? (pomodoroCount % 4 === 0 ? LONG_BREAK_DURATION : BREAK_DURATION) : WORK_DURATION))}
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-xl font-bold ${
                  timerMode === 'break' ? 'text-green-600' :
                  timerMode === 'work' ? 'text-red-600' : 'text-gray-400'
                }`}>
                  {Math.ceil(timeRemaining / 60)} dk
                </span>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex justify-center gap-3">
            {timerMode === 'idle' && (
              <button
                onClick={startTimer}
                className="flex items-center gap-2 px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition"
              >
                <PlayIcon className="h-5 w-5" />
                Baslat
              </button>
            )}
            {timerMode === 'work' && (
              <>
                <button
                  onClick={pauseTimer}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white rounded-xl font-medium hover:bg-yellow-600 transition"
                >
                  <PauseIcon className="h-5 w-5" />
                  Duraklat
                </button>
                <button
                  onClick={cancelTimer}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-xl font-medium hover:bg-gray-600 transition"
                >
                  <StopIcon className="h-5 w-5" />
                  Iptal
                </button>
              </>
            )}
            {timerMode === 'paused' && (
              <>
                <button
                  onClick={resumeTimer}
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition"
                >
                  <PlayIcon className="h-5 w-5" />
                  Devam Et
                </button>
                <button
                  onClick={cancelTimer}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-xl font-medium hover:bg-gray-600 transition"
                >
                  <StopIcon className="h-5 w-5" />
                  Iptal
                </button>
              </>
            )}
            {timerMode === 'break' && (
              <button
                onClick={skipBreak}
                className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition"
              >
                <ForwardIcon className="h-5 w-5" />
                Molayi Atla
              </button>
            )}
          </div>

          {/* Info Text */}
          <p className="text-xs text-gray-400 text-center mt-4">
            Her pomodoro tamamlandiginda otomatik olarak calisma kaydi olusturulur
          </p>
        </div>

        {/* Study Log Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <PencilSquareIcon className="h-5 w-5 mr-2 text-blue-600" />
            Calisma Kaydi Ekle
          </h2>

          <div className="space-y-4">
            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bugun ne kadar calistin?
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="12"
                    value={hours}
                    onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                    className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  />
                  <span className="text-gray-600">saat</span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    step="5"
                    value={minutes}
                    onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                    className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-center"
                  />
                  <span className="text-gray-600">dakika</span>
                </div>
              </div>
            </div>

            {/* Activities */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ne yaptin?
              </label>
              <div className="flex flex-wrap gap-2">
                {activityOptions.map(activity => (
                  <button
                    key={activity.id}
                    onClick={() => handleActivityToggle(activity.id)}
                    className={`px-4 py-2 rounded-lg border transition ${
                      activities.includes(activity.id)
                        ? 'bg-blue-100 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {activity.emoji} {activity.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Questions - shown if questions activity is selected */}
            {activities.includes('questions') && (
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Soru istatistikleri
                </label>
                <div className="flex items-center space-x-4">
                  <div>
                    <input
                      type="number"
                      min="0"
                      value={questionsAttempted}
                      onChange={(e) => setQuestionsAttempted(parseInt(e.target.value) || 0)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center"
                    />
                    <span className="ml-2 text-sm text-gray-600">cozulen</span>
                  </div>
                  <div>
                    <input
                      type="number"
                      min="0"
                      max={questionsAttempted}
                      value={questionsCorrect}
                      onChange={(e) => setQuestionsCorrect(parseInt(e.target.value) || 0)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center"
                    />
                    <span className="ml-2 text-sm text-gray-600">dogru</span>
                  </div>
                </div>
              </div>
            )}

            {/* Progress Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ilerleme guncelle: <span className="text-blue-600 font-bold">{progress}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={(e) => setProgress(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Not (opsiyonel)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notlarini buraya yaz..."
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveStudyLog}
              disabled={saving}
              className={`w-full py-3 rounded-lg font-semibold transition ${
                saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg text-white'
              }`}
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>

        {/* Study Logs History */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-purple-600" />
            Gecmis Kayitlar
          </h2>

          {topicProgress?.studyLogs?.logs?.length > 0 ? (
            <div className="space-y-3">
              {topicProgress.studyLogs.logs.map((log, index) => (
                <div
                  key={log.id || index}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      {new Date(log.date).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long'
                      })}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDuration(log.durationMinutes)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <span className="text-green-600 font-medium">
                      %{log.progressBefore} → %{log.progressAfter}
                    </span>
                    {log.questionsAttempted > 0 && (
                      <span className="text-blue-600">
                        {log.questionsCorrect}/{log.questionsAttempted} soru
                      </span>
                    )}
                  </div>
                  {log.note && (
                    <p className="mt-2 text-sm text-gray-600 italic">"{log.note}"</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              Henuz calisma kaydi yok
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicDetailPage;
